import { useState, useEffect, useMemo } from "react";
import { i18n } from "../../../../localization/i18n";

import { showToast } from "../../../../components/CustomToast";
import { StepPersonalData } from "./Components/StepPersonalData";
import { StepDelivery } from "./Components/StepDelivery";
import { StepCheckout } from "./Components/StepCheckout";
import { StepSuccess } from "./Components/StepSuccess";

import api from "../../../../services/api";

import { customerSituations, personTypes, roles } from "../../../../utils/constants";
import LogoImg from "../../../../assets/logo.svg";

import {
  Container,
  ProductBanner,
  ProductThumbnail,
  ProductTitle,
  FormContainer,
  StepperContainer,
  StepTab,
} from "./styles";

import { Loading } from "../../../../components/Loading";
import { IAuthFormData, IPersonFormData, ICheckoutData, ICourse } from "./dtos.d";

export function CourseRegistration() {
  const [coursesList, setCoursesList] = useState<ICourse[]>([]);
  const [isFetchingCourses, setIsFetchingCourses] = useState(true);
  
  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  
  const [authFormData, setAuthFormData] = useState<IAuthFormData>();
  const [personFormData, setPersonFormData] = useState<IPersonFormData>({
    course_id: "",
    name: "",
    email: "",
    cpf: "",
    expertise: "",
    professional_number: "",
    cellphone: "",
    cep: "",
    address: "",
    number: "",
    noNumber: false,
    complement: "",
    noComplement: false,
    district: "",
    city: "",
    state: "",
    hasHeatGun: true,
    heatGunVoltage: ""
  });
  const [checkoutData, setCheckoutData] = useState<ICheckoutData>({
    paymentPlan: null,
    installments: 1,
    totalValue: 0,
    useStudentData: true,
    billingType: "FISICA",
    billingName: "",
    billingDocument: "",
    billingEmail: "",
    useDeliveryAddressForBilling: true,
    billingCep: "",
    billingAddress: "",
    billingNumber: "",
    billingNoNumber: false,
    billingComplement: "",
    billingNoComplement: false,
    billingDistrict: "",
    billingCity: "",
    billingState: ""
  });

  const [transactionCode, setTransactionCode] = useState<string>("");
  const [paymentLink, setPaymentLink] = useState<string>("");

  useEffect(() => {
    api.get("/courses?active=true").then((response) => {
      const data = response.data;
      let list: ICourse[] = Array.isArray(data) ? data : (data?.courses || data?.data || []);
      
      if (list.length > 0) {
        // Ordena do mais antigo pro mais novo baseado no created_at
        list = list.sort((a, b) => {
          if (!a.created_at) return 1;
          if (!b.created_at) return -1;
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        });

        // Auto-seleciona o primeiro da lista (o mais antigo)
        setPersonFormData(prev => ({
          ...prev,
          course_id: list[0].id
        }));
      }

      setCoursesList(list);
    }).catch((err) => {
      showToast({ type: "error", message: "Erro ao buscar a lista de cursos." });
      console.error(err);
    }).finally(() => {
      setIsFetchingCourses(false);
    });
  }, []);

  const selectedCourseData = useMemo(() => {
    if (!personFormData.course_id) return null;
    return coursesList.find(c => c.id === personFormData.course_id) || null;
  }, [personFormData.course_id, coursesList]);

  const nextStep = () => setActiveStep((prevStep) => prevStep + 1);
  const prevStep = () => setActiveStep((prevStep) => prevStep - 1);


  const steps = [
    {
      id: "personal",
      label: "Dados do Participante",
    },
    {
      id: "delivery",
      label: "Endereço",
    },
    {
      id: "checkout",
      label: "Pagamento",
    },
    {
      id: "success",
      label: "Obrigado",
    },
  ];

  return (
    <>
      <Loading
        loading={isFetchingCourses}
        text="Carregando informações..."
        numberOfDots={3}
        dotSymbol="."
      />
      {!isFetchingCourses && (
        <Container>
          <ProductBanner>
            <ProductThumbnail>
              <img src={LogoImg} alt="Fix it 3D" />
            </ProductThumbnail>
            <ProductTitle>Curso Órteses Fix it 3D</ProductTitle>
          </ProductBanner>

          <FormContainer>
            {activeStep < 3 && (
              <StepperContainer>
                {steps.slice(0, 3).map((step, index) => {
                  const isActive = activeStep === index;
                  const isCompleted = activeStep > index;

                  return (
                    <StepTab key={index} active={isActive} completed={isCompleted}>
                      <div className="step-circle">{isCompleted ? "✓" : index + 1}</div>
                      {step.label}
                    </StepTab>
                  );
                })}
              </StepperContainer>
            )}

            {steps[activeStep]?.id === "personal" && (
              <StepPersonalData
                coursesList={coursesList}
                personFormData={personFormData}
                setPersonFormData={setPersonFormData}
                nextStep={nextStep}
                loading={loading}
              />
            )}
            
            {steps[activeStep]?.id === "delivery" && (
              <StepDelivery
                personFormData={personFormData}
                setPersonFormData={setPersonFormData}
                nextStep={nextStep}
                prevStep={prevStep}
                loading={loading}
              />
            )}
            
            {steps[activeStep]?.id === "checkout" && (
              <StepCheckout
                courseId={personFormData.course_id}
                courseData={selectedCourseData}
                personFormData={personFormData}
                checkoutData={checkoutData}
                setCheckoutData={setCheckoutData}
                nextStep={nextStep}
                prevStep={prevStep}
                loading={loading}
                setLoading={setLoading}
                transactionCode={transactionCode}
                setTransactionCode={setTransactionCode}
                paymentLink={paymentLink}
                setPaymentLink={setPaymentLink}
              />
            )}

            {steps[activeStep]?.id === "success" && (
              <StepSuccess
                authFormData={authFormData}
                setAuthFormData={setAuthFormData}
                personFormData={personFormData}
                checkoutData={checkoutData}
                loading={loading}
                setLoading={setLoading}
                prevStep={prevStep}
                transactionCode={transactionCode}
              />
            )}
          </FormContainer>
        </Container>
      )}
    </>
  );
}

export default CourseRegistration;
